import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Hexagon, Mail, Lock, AlertCircle, Shield, ArrowLeft } from 'lucide-react';
interface LoginProps {
  onLogin: () => void;
}
export function Login({
  onLogin
}: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [mfaError, setMfaError] = useState('');
  const navigate = useNavigate();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (showMFA && inputRefs.current[0]) {
      inputRefs.current[0]?.focus();
    }
  }, [showMFA]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (email === 'sven.schulte@pezula.net' && password === 'Letmein0815') {
      setShowMFA(true);
      setIsLoading(false);
    } else {
      setError('Ungültige Anmeldedaten. Versuchen Sie admin/admin');
      setIsLoading(false);
    }
  };
  const handleMFAChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...mfaCode];
    newCode[index] = value;
    setMfaCode(newCode);
    setMfaError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (index === 5 && value) {
      const fullCode = newCode.join('');
      verifyMFACode(fullCode);
    }
  };
  const handleMFAKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleMFAPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setMfaCode(digits);
      inputRefs.current[5]?.focus();
      verifyMFACode(pastedData);
    }
  };
  const verifyMFACode = async (code: string) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (code === '123456') {
      localStorage.setItem('isAuthenticated', 'true');
      onLogin();
      navigate('/');
    } else {
      setMfaError('Ungültiger Code. Versuchen Sie 123456');
      setMfaCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setIsLoading(false);
    }
  };
  const handleBackToLogin = () => {
    setShowMFA(false);
    setMfaCode(['', '', '', '', '', '']);
    setMfaError('');
    setPassword('');
  };
  const handleSocialLogin = async (provider: string) => {
    try {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 800));
      localStorage.setItem('isAuthenticated', 'true');
      onLogin();
      navigate('/');
    } catch (err) {
      console.error('Social login error:', err);
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-id="element-798">
      <div className="absolute inset-0 opacity-10" data-id="element-799">
        <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} data-id="element-800" />
      </div>

      <div className="w-full max-w-md relative z-10" data-id="element-801">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8" data-id="element-802">
          <div className="flex items-center justify-center mb-8" data-id="element-803">
            <div className="relative" data-id="element-804">
              <Hexagon className="w-16 h-16 text-blue-400" strokeWidth={1.5} data-id="element-805" />
              <Shield className="w-8 h-8 text-blue-300 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-806" />
            </div>
          </div>

          <div className="text-center mb-8" data-id="element-807">
            <h1 className="text-3xl font-bold text-white mb-2" data-id="element-808">
              {showMFA ? 'Zwei-Faktor-Authentifizierung' : 'neocube ONE'}
            </h1>
            <p className="text-slate-300" data-id="element-809">
              {showMFA ? 'Geben Sie den 6-stelligen Code ein' : 'Melden Sie sich bei Ihrem Konto an'}
            </p>
          </div>

          <div className="text-center mb-8" data-id="element-810">
            <p className="text-sm text-blue-300 font-medium" data-id="element-811"></p>
          </div>

          {!showMFA ? <form onSubmit={handleSubmit} className="space-y-6" data-id="element-812">
              {error && <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3" data-id="element-813">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-814" />
                  <p className="text-red-200 text-sm" data-id="element-815">{error}</p>
                </div>}

              <div className="space-y-4" data-id="element-816">
                <div data-id="element-817">
                  <label className="block text-sm font-medium text-slate-200 mb-2" data-id="element-818">
                    E-Mail oder Benutzername
                  </label>
                  <div className="relative" data-id="element-819">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-820" />
                    <Input type="text" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-slate-900 placeholder-slate-400 focus:border-blue-400" placeholder="admin" required data-id="element-821" />
                  </div>
                </div>

                <div data-id="element-822">
                  <label className="block text-sm font-medium text-slate-200 mb-2" data-id="element-823">
                    Passwort
                  </label>
                  <div className="relative" data-id="element-824">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-825" />
                    <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-slate-900 placeholder-slate-400 focus:border-blue-400" placeholder="admin" required data-id="element-826" />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600 text-white" disabled={isLoading} data-id="element-827">
                {isLoading ? 'Anmeldung läuft...' : 'Anmelden'}
              </Button>
            </form> : <div className="space-y-6" data-id="element-828">
              {mfaError && <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3" data-id="element-829">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-830" />
                  <p className="text-red-200 text-sm" data-id="element-831">{mfaError}</p>
                </div>}

              <div className="flex gap-2 justify-center" onPaste={handleMFAPaste} data-id="element-832">
                {mfaCode.map((digit, index) => <input key={index} ref={el => inputRefs.current[index] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleMFAChange(index, e.target.value)} onKeyDown={e => handleMFAKeyDown(index, e)} className="w-12 h-14 text-center text-2xl font-bold bg-white/5 border border-white/10 rounded-lg text-white focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50" disabled={isLoading} data-id="element-833" />)}
              </div>

              <Button onClick={handleBackToLogin} variant="outline" className="w-full border-white/10 text-slate-200 hover:bg-white/5" data-id="element-834">
                <ArrowLeft className="w-4 h-4 mr-2" data-id="element-835" />
                Zurück zur Anmeldung
              </Button>
            </div>}

          <div className="mt-6 text-center" data-id="element-836">
            <p className="text-xs text-slate-400" data-id="element-837">
              Demo: admin/admin → Code: 123456
            </p>
          </div>
        </div>

        <div className="mt-4 text-center" data-id="element-838">
          <p className="text-xs text-slate-400" data-id="element-839">
            powered by neocube GmbH - 2025
          </p>
          <p className="text-xs text-slate-400 mt-1" data-id="element-840">Version 251208-v59</p>
        </div>
      </div>
    </div>;
}